/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import Image from 'next/image';
import { useLanguage } from '@/providers/LanguageProvider';
import Close from 'public/shared-icons/Close.svg';
import { PostEvalsQueryBodyType } from 'types/api-data';
import { IPrompt } from 'types/prompt';

import { useEvals } from '@/hooks/useEvals';
import { usePrompts } from '@/hooks/usePrompts';

import { CustomButton } from '../common';
import { Icons } from '../icons';
import { NotFoundMessage } from '../not-found';
import Loader from '../spinner/loader';
import { Dialog, DialogTrigger } from '../ui/dialog';
import { Separator } from '../ui/separator';
import { PromptDetailsModal } from './prompt-detail-modal';
import { PromptListAll } from './prompt-list';
import { SelectPromptGroup } from './select-group-prompt';

export const itemsPerPage = Number(
  process.env.NEXT_PUBLIC_PROMPTS_ITEMS_PER_PAGE
);
export const groupsPerPage = Number(
  process.env.NEXT_PUBLIC_PROMPTS_GROUPS_PER_PAGE
);

export const PromptsListing = ({
  data,
  currentPage,
  setCurrentPage,
  selectedPrompt,
  setSelectedPrompt,
  appName,
  workflowName,
  selectedGrouping,
  setSelectedGrouping,
}: {
  data: any;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  selectedPrompt: IPrompt[];
  setSelectedPrompt: Dispatch<SetStateAction<IPrompt[]>>;
  appName?: string;
  workflowName?: string;
  selectedGrouping: string;
  setSelectedGrouping: Dispatch<SetStateAction<string>>;

  activeTab: 'time' | 'search';
}) => {
  const [accordionValue, setAccordionValue] = useState<string>('item-0');
  const { messages } = useLanguage();
  const langData = messages?.PromptsListing;

  const { state } = usePrompts();
  const { evaluateActions } = useEvals();

  const factsData = state?.factsData;
  const start_time = state.queryParams.start_time;
  const end_time = state.queryParams.end_time;
  // maybe used later
  // const isSearching =
  //   activeTab === 'time' ? state.promptLoading : state.promptByTraceIdLoading;

  useEffect(() => {
    if (
      state.factName === 'inferences' &&
      data?.length > 0 &&
      (appName || workflowName)
    ) {
      const fact_ids = data.map(
        (item: IPrompt) => `${item.trace_id}.${item.span_id}`
      );

      const body: PostEvalsQueryBodyType = {
        fact_name: 'inferences',
        fact_ids,
        start_time: start_time ?? undefined,
        end_time: end_time ?? undefined,
      };
      evaluateActions.fetchEvalsTemplate(
        'inferences',
        appName ?? undefined,
        start_time ?? undefined,
        end_time ?? undefined
      );
      evaluateActions.fetchEvalsReport(appName ?? '', body, workflowName);
    }
  }, [data, state.factName, appName, workflowName]);

  useEffect(() => {
    if (currentPage === 1) {
      setAccordionValue('item-0');
    } else {
      setAccordionValue('');
    }
  }, [currentPage]);

  const promptList =
    selectedGrouping.length > 0
      ? data.reduce((acc: Record<string, IPrompt[]>, prompt: any) => {
          let key: string | undefined;
          if (selectedGrouping === 'model_name') {
            key = prompt[selectedGrouping];
          } else if (selectedGrouping === 'provider_name') {
            key = prompt?.model_attributes?.provider_name;
          } else if (selectedGrouping === 'service') {
            key = prompt?.model_attributes?.service_name;
          } else if (selectedGrouping === 'prompt_template') {
            key = prompt?.prompt_template_name;
          }

          if (!key && selectedGrouping === 'prompt_template') {
            acc['Unknown'] = acc['Unknown'] || [];
            acc['Unknown'].push(prompt);
          } else if (!key) {
            acc['Default'] = acc['Default'] || [];
            acc['Default'].push(prompt);
          } else {
            acc[key] = acc[key] || [];
            acc[key].push(prompt);
          }
          return acc;
        }, {})
      : data;

  let sortPromptByGroupName = {};
  if (selectedGrouping.length > 1) {
    const sortByGroupKeys = Object.keys(promptList).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );

    sortPromptByGroupName = Object.fromEntries(
      sortByGroupKeys.map((key) => [
        key,
        promptList[key].sort(
          (a: { start_time: string }, b: { start_time: string }) =>
            new Date(b.start_time).getTime() - new Date(a.start_time).getTime()
        ),
      ])
    );
  }

  const getPage = (start: number) => {
    const entries = Object.entries(sortPromptByGroupName);
    const end = start + groupsPerPage;
    const pageEntries = entries.slice(start, end);
    return Object.fromEntries(pageEntries);
  };

  const startIndex =
    selectedGrouping.length > 0
      ? (currentPage - 1) * groupsPerPage
      : (currentPage - 1) * itemsPerPage;

  const currentPrompts =
    selectedGrouping.length > 0
      ? getPage(startIndex)
      : promptList?.slice(startIndex, startIndex + itemsPerPage);

  const hasPrompts = data?.length > 0;

  return (
    <div className="h-full pb-2 text-white">
      <div className="flex flex-col justify-between rounded-md pb-3">
        <div>
          {!state?.promptLoading && hasPrompts && (
            <div className="flex h-12 w-full items-center border-b border-line-tertiary bg-[#FFFFFF0A]">
              <div className="ml-4 flex items-center gap-4">
                <Dialog>
                  <DialogTrigger asChild>
                    <CustomButton
                      customVariant="secondary"
                      customSize="regular"
                      disabled={selectedPrompt.length < 2}
                      className="border-none bg-none hover:bg-transparent hover:text-inherit focus:ring-0 focus:ring-offset-0"
                    >
                      {Icons.CompareIcon('size-4 cursor-pointer')}
                      <span className="text-form-input-tet">
                        {langData?.compare}
                      </span>
                    </CustomButton>
                  </DialogTrigger>
                  <PromptDetailsModal
                    data={selectedPrompt}
                    factsData={factsData}
                    compare
                  />
                </Dialog>

                {selectedPrompt.length > 0 && (
                  <div className="flex items-center">
                    <p className="border-r border-[#0A343C] pr-3 text-body text-body-subtle">
                      {selectedPrompt.length} {langData?.item}
                      {selectedPrompt.length > 1 && messages?.General?.s}{' '}
                      {langData?.selected}
                    </p>

                    <CustomButton
                      variant="link"
                      className="flex font-normal text-link-active hover:no-underline"
                      onClick={() => setSelectedPrompt([])}
                    >
                      <div className="mr-1 h-3 w-3 p-[1px]">
                        <Image
                          src={Close}
                          width={100}
                          height={100}
                          alt="close icon"
                        />
                      </div>
                      <span>{langData?.clear_selection}</span>
                    </CustomButton>
                  </div>
                )}
              </div>
              <Separator
                className="mx-4 h-6 bg-line-tertiary px-px"
                orientation="vertical"
              />
              <div className="flex items-center gap-2">
                <p className="text-form-input-text">{langData?.group_by}</p>
                <SelectPromptGroup
                  selectedGrouping={selectedGrouping}
                  setSelectedGrouping={setSelectedGrouping}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </div>
          )}
          {state?.promptLoading ? (
            <div className="flex h-[814px] flex-1 items-center justify-center">
              <Loader
                title={langData?.getting_prompts}
                description={messages?.General.please_wait}
              />
            </div>
          ) : hasPrompts ? (
            <div className="px-2">
              <PromptListAll
                accordionValue={accordionValue}
                setAccordionValue={setAccordionValue}
                currentPrompts={currentPrompts}
                selectedPrompt={selectedPrompt}
                setSelectedPrompt={setSelectedPrompt}
                selectedGrouping={selectedGrouping}
                startIndex={startIndex}
              />
            </div>
          ) : (
            <NotFoundMessage
              title={langData?.no_prompts_found}
              outerDivClassName="h-[814px] my-0"
            />
          )}
        </div>
      </div>
    </div>
  );
};
